import React, { FormEvent, useState, useEffect } from 'react';
import { Grid } from '@mui/material';
import Appbar from 'components/appbar/Appbar';
import { api } from 'services/api';
import { useMessaging } from 'providers/messaging';
import history from 'services/history';
import Loading from 'components/loading/Loading';
import { useParams } from 'react-router-dom';
import InsideLoading from 'components/loading/InsideLoading';
import AccessRuleActions from './ResourceUpdateActions';
import AccessRuleForm from '../ResourceForm';
import PageHeader from 'components/page-header/PageHeader';
import { useResourceValidation } from '../validation/resourceValidation';
import { Resource } from 'types/resource';

export type AccessRuleValidation = {
  name?: string;
  title?: string;
  description?: string;
};

const ResourceUpdate: React.FC = () => {
  const [resource, setResource] = useState<Resource>({} as Resource);
  const [validation, setValidation, validate] = useResourceValidation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { handleOpen } = useMessaging();
  const { id } = useParams<{ id: string }>();
  const [resources, setResources] = useState<Resource[]>([]);

  useEffect(() => {
    if (!id) return;

    async function loadResource() {
      const response = await api.get(`/resources/${id}`);
      setResource(response.data);
    }

    function loadResources() {
      return api
        .get('/resources')
        .then(response => {
          setResources(response.data);
        })
        .catch(err => {
          console.error(err);
        });
    }

    Promise.all([loadResource(), loadResources()])
      .catch(() => {
        handleOpen('Não foi possível carregar o a regra de acesso');
        history.push('/access-rules');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [handleOpen, id]);

  function handleChange(index: keyof Resource, value: any) {
    setResource(oldValue => ({
      ...oldValue,
      [index]: value,
    }));
  }

  function handleValidation(e?: FormEvent<HTMLFormElement>) {
    e?.preventDefault();

    setValidation({});

    validate(resource)
      .then(() => {
        handleSubmit();
      })
      .catch(err => {
        setValidation({
          [err.path]: err.message,
        });
      });
  }

  function handleSubmit() {
    setSaving(true);

    api
      .put(`/resources/${id}`, resource)
      .then(() => {
        handleOpen('Salvo');
        setLoading(false);
        history.push('/resources');
      })
      .catch(err => {
        setSaving(false);
        if (err.response) handleOpen(err.response.data.error);
        else handleOpen('Não foi possível salvar a regra de acesso');
      });
  }

  function handleDelete() {
    setSaving(true);

    api
      .delete(`/resources/${id}`)
      .then(() => {
        setLoading(false);
        history.push('/resources');
        handleOpen('Recurso excluído');
      })
      .catch(err => {
        setSaving(false);
        if (err.response) handleOpen(err.response.data.error);
        else handleOpen('Não foi possível salvar a regra de acesso');
      });
  }

  return (
    <>
      {saving && <Loading />}
      <Appbar
        backAction={() => history.push('/resources')}
        title="Recurso"
        ActionsComponent={<AccessRuleActions handleSubmit={handleValidation} handleDelete={handleDelete} />}
      />
      <PageHeader title="Editar recurso" />
      {loading ? (
        <InsideLoading />
      ) : (
        <Grid container direction="column">
          <Grid item xs={12} lg={4} xl={4} md={6}>
            <form onSubmit={handleValidation}>
              <AccessRuleForm
                handleChange={handleChange}
                resource={resource}
                validation={validation}
                resources={resources}
              />
              <button type="submit" style={{ display: 'none' }} />
            </form>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default ResourceUpdate;
